import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";

/**
 * ShoppeTransitionChamber — editorial "transition chamber" that bridges the
 * physical storefront (PlacePresence) into the deeper interior/shop
 * experience (VisitTheShoppe). Soft botanical darkness, ambient drift,
 * restrained motion, museum-style pacing. NOT promotional.
 */
const ShoppeTransitionChamber = () => {
  return (
    <section
      aria-labelledby="shoppe-transition-heading"
      className="relative overflow-hidden pt-14 pb-20 md:pt-16 md:pb-24 -mt-10 md:-mt-16 -mb-24 md:-mb-32"
      style={{
        background:
          "radial-gradient(ellipse at 50% 35%, hsl(150 28% 13%) 0%, hsl(150 32% 8%) 72%)",
      }}
    >
      {/* ambient drifting glow */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-60 ambient-drift motion-reduce:animate-none"
        style={{
          background:
            "radial-gradient(circle at 50% 45%, hsla(38, 65%, 55%, 0.16), transparent 65%)",
        }}
      />

      {/* slow breathing botanical shadow */}
      <div
        aria-hidden="true"
        className="absolute -bottom-32 left-1/2 -translate-x-1/2 w-[120%] h-[60%] opacity-30 motion-reduce:animate-none"
        style={{
          background:
            "radial-gradient(ellipse at center, hsla(150, 40%, 22%, 0.6), transparent 70%)",
          animation: "ambient-drift 22s ease-in-out infinite",
        }}
      />

      {/* faint drifting grain */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-[0.08] mix-blend-overlay ambient-drift motion-reduce:animate-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='320' height='320'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* subtle vignette */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 55%, hsl(150 35% 5% / 0.6) 100%)",
        }}
      />

      {/* top fade — dissolves PlacePresence cream into the dark chamber */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-32 md:h-44 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, hsl(40 30% 95%) 0%, hsl(40 30% 95% / 0.55) 35%, transparent 100%)",
        }}
      />

      {/* bottom fade — dissolves the chamber into VisitTheShoppe cream */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-56 md:h-72 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, transparent 0%, hsl(40 30% 95% / 0.45) 45%, hsl(40 30% 95%) 100%)",
        }}
      />

      <div className="relative container mx-auto px-6 max-w-3xl text-center">
        <Reveal duration={1100}>
          <span className="inline-flex items-center gap-3 font-mono text-[0.6rem] tracking-[0.36em] uppercase text-[hsl(var(--gold))]">
            <span aria-hidden className="block h-px w-6 bg-current opacity-60" />
            Before You Enter
            <span aria-hidden className="block h-px w-6 bg-current opacity-60" />
          </span>
        </Reveal>

        <Reveal duration={1200} delay={120}>
          <h2
            id="shoppe-transition-heading"
            className="mt-6 font-serif text-[2.1rem] sm:text-[2.6rem] md:text-[3.25rem] leading-[1.1] tracking-[-0.01em] text-[hsl(var(--cream))]"
          >
            The Storefront Became{" "}
            <em className="not-italic text-[hsl(var(--gold))]">
              More Than Retail.
            </em>
          </h2>
        </Reveal>

        <Reveal duration={1200} delay={220}>
          <p className="mt-5 font-serif text-lg md:text-xl leading-[1.75] text-[hsl(var(--cream))]/80 max-w-2xl mx-auto">
            What began as handmade experimentation slowly evolved into a
            gathering place for wellness, conversation, ritual, mentorship,
            and Caribbean memory.
          </p>
        </Reveal>

        <Reveal duration={1200} delay={340}>
          <div className="mt-9 flex flex-col items-center">
            <span
              aria-hidden
              className="block h-px w-10 bg-gradient-to-r from-transparent via-[hsl(var(--gold))] to-transparent mb-5"
            />
            <p className="font-serif italic text-base md:text-lg text-[hsl(var(--cream))]/65 max-w-xl leading-relaxed">
              “Some visitors arrived for products.
              <br className="hidden sm:block" />
              Others stayed for the stories.”
            </p>
            <span
              aria-hidden
              className="block h-px w-10 bg-gradient-to-r from-transparent via-[hsl(var(--gold))] to-transparent mt-5"
            />
          </div>
        </Reveal>

        <Reveal duration={1100} delay={460}>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/visit-di-organics-shoppe"
              className="group inline-flex items-center gap-2 px-7 py-3 border border-[hsl(var(--gold))]/50 text-[hsl(var(--cream))] font-sans text-[11px] tracking-[0.3em] uppercase rounded-[2px] transition-all duration-500 hover:border-[hsl(var(--gold))] hover:bg-[hsl(var(--gold))]/10 hover:shadow-[0_0_24px_-6px_hsl(38_65%_55%/0.6)]"
            >
              Visit Di Organics Shoppe
              <span
                aria-hidden
                className="transition-transform duration-500 group-hover:translate-x-1"
              >
                →
              </span>
            </Link>
            <Link
              to="/community"
              className="group inline-flex items-center gap-2 px-7 py-3 text-[hsl(var(--cream))]/80 font-sans text-[11px] tracking-[0.3em] uppercase rounded-[2px] transition-all duration-500 hover:text-[hsl(var(--gold))]"
            >
              Explore the Community Archive
              <span
                aria-hidden
                className="transition-transform duration-500 group-hover:translate-x-1"
              >
                →
              </span>
            </Link>
          </div>
        </Reveal>

        <Reveal duration={1100} delay={600}>
          <p className="mt-10 font-mono text-[0.58rem] tracking-[0.34em] uppercase text-[hsl(var(--cream))]/40">
            Field Notes · Shadwell Estate, St. Kitts
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default ShoppeTransitionChamber;
